import PersonOffOutlined from '@mui/icons-material/PersonOffOutlined'
import { Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { useGetPositionHolders } from '@/services/hooks/relay/positions'
import { ResolvedPositionHolder } from '@/types/position-holders'
import PositionAvatar from '@/components/avatars/PositionAvatar'
import UserAvatar from '@/components/avatars/UserAvatar'

type PositionTagPopoverProps = {
  positionId: string
  documentId: string
  name: string
  open: boolean
}

type HolderListProps = {
  holders: ResolvedPositionHolder[]
  isPending: boolean
  isError: boolean
}

const NO_ACCOUNT_WARNING =
  'This person does not have a Pixydocs account. They will not receive notifications.'
const NOT_SIGNED_IN_WARNING =
  'This person has been invited but has not signed in to Pixydocs yet. They will not receive notifications.'

function holderWarning(holder: ResolvedPositionHolder) {
  if (!holder.user_id) return NO_ACCOUNT_WARNING
  if (holder.user_status && holder.user_status !== 'active') {
    return NOT_SIGNED_IN_WARNING
  }
  return null
}

function HolderSkeletons() {
  return (
    <>
      {[0, 1, 2].map((index) => (
        <Stack key={index} direction="row" spacing={1.5} alignItems="center">
          <UserAvatar loading />
          <Skeleton variant="text" width="60%" />
        </Stack>
      ))}
    </>
  )
}

function HolderList({ holders, isPending, isError }: HolderListProps) {
  if (isPending) return <HolderSkeletons />
  if (isError) {
    return (
      <Typography variant="body2" color="text.secondary">
        Couldn&apos;t load holders
      </Typography>
    )
  }
  if (holders.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No holders
      </Typography>
    )
  }
  return holders.map((holder) => {
    const displayName = holder.user_name ?? holder.name
    const warning = holderWarning(holder)
    return (
      <Stack
        key={holder.holder_id}
        direction="row"
        spacing={1.5}
        alignItems="center"
      >
        <UserAvatar name={displayName} />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2">{displayName}</Typography>
          {displayName !== holder.name && (
            <Typography variant="caption" color="text.secondary">
              ERP: {holder.name}
            </Typography>
          )}
          {holder.email && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ wordBreak: 'break-word' }}
            >
              {holder.email}
            </Typography>
          )}
        </Stack>
        {warning && (
          <Tooltip title={warning} disableInteractive>
            <PersonOffOutlined
              aria-label={warning}
              sx={{ fontSize: 18, color: 'text.secondary' }}
            />
          </Tooltip>
        )}
      </Stack>
    )
  })
}

export default function PositionTagPopover({
  positionId,
  documentId,
  name,
  open,
}: PositionTagPopoverProps) {
  const { holders, isPending, isError } = useGetPositionHolders({
    positionId,
    documentId,
    enabled: open,
    retry: false,
    staleTime: 2 * 60 * 1000,
  })

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <PositionAvatar />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Held by
      </Typography>
      <Stack spacing={1} sx={{ maxHeight: 240, overflowY: 'auto' }}>
        <HolderList holders={holders} isPending={isPending} isError={isError} />
      </Stack>
    </Stack>
  )
}
